import { useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import clsx from "clsx";
import { Button, CircleLoader, Message, SectionHeading } from "../components";
import { useSite } from "../../store";
import {
  getDeviceId,
  getDjToken,
  setDjToken,
  clearDjToken,
} from "../../utils";
import {
  listSongRequests,
  requestSong,
  setSongRequestStatus,
  toggleSongLike,
  verifyDjAccess,
  type RequestSongOutcome,
  type SongRequestItem,
  type SongResult,
  type SongStatus,
} from "../../services";
import { DjBar, SearchPanel, SongList } from "./parts";
import "./DjRequests.scss";

const POLL_MS = 15000;

const ADDED_OUTCOMES = new Set<RequestSongOutcome>([
  "created",
  "voted",
  "resurrected",
  "already",
]);

interface Notice {
  variant: "primary" | "success" | "error";
  text: string;
}

/**
 * The DJ Requests page. Guests search MusicBrainz (via the backend proxy),
 * add songs and upvote the shared list; a per-wedding PIN unlocks DJ mode in
 * place, where the DJ confirms (plays), rejects or restores requests. Gated by
 * the `allowDjRequests` permission (see App routing). The list has no realtime
 * channel (Firestore is locked down), so it polls while the tab is visible.
 */
export const DjRequests = () => {
  const { t } = useTranslation();
  const { siteId } = useSite();
  const deviceId = useMemo(() => getDeviceId(), []);

  const [djToken, setDjTokenState] = useState<string | null>(() =>
    getDjToken(siteId),
  );
  const isDj = djToken !== null;

  const [items, setItems] = useState<SongRequestItem[]>([]);
  const [loaded, setLoaded] = useState(false);
  const [listError, setListError] = useState(false);
  const [sort, setSort] = useState<"top" | "newest">("top");
  const [djTab, setDjTab] = useState<"active" | "done" | "rejected">("active");
  const [notice, setNotice] = useState<Notice | null>(null);
  const [pendingReadd, setPendingReadd] = useState<SongResult | null>(null);

  const loadList = useCallback(async () => {
    let status: SongStatus | undefined;
    if (isDj) {
      if (djTab === "done") status = "played";
      else if (djTab === "rejected") status = "rejected";
      else status = "pending";
    }
    try {
      const res = await listSongRequests({
        siteId,
        deviceId,
        djKey: djToken ?? undefined,
        status,
        sort,
      });
      setItems(res.requests);
      setListError(false);
      if (djToken && !res.isDj) {
        // Token expired/invalid — drop back to guest mode.
        clearDjToken(siteId);
        setDjTokenState(null);
      }
    } catch {
      setListError(true);
    } finally {
      setLoaded(true);
    }
  }, [siteId, deviceId, djToken, isDj, djTab, sort]);

  useEffect(() => {
    loadList();
    const id = window.setInterval(() => {
      if (document.visibilityState === "visible") loadList();
    }, POLL_MS);
    return () => window.clearInterval(id);
  }, [loadList]);

  const applyOutcome = useCallback(
    (status: RequestSongOutcome) => {
      if (status === "rejectedBlocked") {
        setNotice({ variant: "primary", text: t("dj.djSetAside") });
      }
    },
    [t],
  );

  const doAdd = useCallback(
    async (song: SongResult, confirmReadd: boolean): Promise<boolean> => {
      try {
        const res = await requestSong({
          siteId,
          deviceId,
          song,
          confirmReadd,
        });
        if (res.status === "needsConfirm") {
          setPendingReadd(song);
          return false;
        }
        applyOutcome(res.status);
        await loadList();
        return ADDED_OUTCOMES.has(res.status);
      } catch {
        setNotice({ variant: "error", text: t("dj.addError") });
        return false;
      }
    },
    [siteId, deviceId, applyOutcome, loadList, t],
  );

  const handleAdd = useCallback(
    (song: SongResult): Promise<boolean> => {
      setNotice(null);
      return doAdd(song, false);
    },
    [doAdd],
  );

  const handleVote = useCallback(
    async (item: SongRequestItem) => {
      const next = !item.likedByMe;
      setItems((prev) =>
        prev.map((i) =>
          i.id === item.id
            ? { ...i, likedByMe: next, likeCount: i.likeCount + (next ? 1 : -1) }
            : i,
        ),
      );
      try {
        await toggleSongLike({
          siteId,
          deviceId,
          requestId: item.id,
          like: next,
        });
      } catch {
        await loadList();
      }
    },
    [siteId, deviceId, loadList],
  );

  const handleStatus = useCallback(
    async (item: SongRequestItem, status: SongStatus) => {
      if (!djToken) return;
      try {
        await setSongRequestStatus({
          siteId,
          requestId: item.id,
          status,
          djKey: djToken,
        });
      } finally {
        await loadList();
      }
    },
    [siteId, djToken, loadList],
  );

  const handleLogin = useCallback(
    async (code: string) => {
      const token = await verifyDjAccess(siteId, code);
      setDjToken(siteId, token);
      setDjTokenState(token);
      setDjTab("active");
    },
    [siteId],
  );

  const handleLogout = useCallback(() => {
    clearDjToken(siteId);
    setDjTokenState(null);
    setDjTab("active");
  }, [siteId]);

  return (
    <div className="body dj-page">
      <div className="dj-topbar">
        <DjBar isDj={isDj} onLogin={handleLogin} onLogout={handleLogout} />
      </div>
      <SectionHeading
        title={t("dj.title")}
        subtitle={isDj ? t("dj.djSubtitle") : t("dj.subtitle")}
      />

      {notice && !isDj && (
        <Message variant={notice.variant}>{notice.text}</Message>
      )}

      {pendingReadd && (
        <div className="dj-confirm">
          <Message variant="warning">{t("dj.alreadyPlayedConfirm")}</Message>
          <div className="dj-confirm-actions">
            <Button appearance="outline" onClick={() => setPendingReadd(null)}>
              {t("dj.cancel")}
            </Button>
            <Button
              onClick={() => {
                const song = pendingReadd;
                setPendingReadd(null);
                if (song) doAdd(song, true);
              }}
            >
              {t("dj.readd")}
            </Button>
          </div>
        </div>
      )}

      {!isDj && <SearchPanel onAdd={handleAdd} />}

      <div className="dj-controls">
        {isDj ? (
          <div className="dj-tabs">
            <button
              type="button"
              className={clsx("dj-tab", { active: djTab === "active" })}
              onClick={() => setDjTab("active")}
            >
              {t("dj.active")}
            </button>
            <button
              type="button"
              className={clsx("dj-tab", { active: djTab === "done" })}
              onClick={() => setDjTab("done")}
            >
              {t("dj.done")}
            </button>
            <button
              type="button"
              className={clsx("dj-tab", { active: djTab === "rejected" })}
              onClick={() => setDjTab("rejected")}
            >
              {t("dj.declined")}
            </button>
          </div>
        ) : (
          <div className="dj-tabs">
            <button
              type="button"
              className={clsx("dj-tab", { active: sort === "top" })}
              onClick={() => setSort("top")}
            >
              {t("dj.top")}
            </button>
            <button
              type="button"
              className={clsx("dj-tab", { active: sort === "newest" })}
              onClick={() => setSort("newest")}
            >
              {t("dj.newest")}
            </button>
          </div>
        )}
      </div>

      {!loaded ? (
        <div className="dj-loading">
          <CircleLoader />
        </div>
      ) : listError ? (
        <Message variant="error">{t("dj.listError")}</Message>
      ) : (
        <SongList
          items={items}
          isDj={isDj}
          djTab={djTab}
          onVote={handleVote}
          onStatus={handleStatus}
        />
      )}
    </div>
  );
};
